/**
 * Reading a `Snapshot` as a game of Apples to Apples. Controls render only because a
 * request is on the snapshot, so no component checks the phase. Asks are looked up by
 * id, not `spec.kind`: three of this game's asks are `choice`.
 */

import type { CoreBlock, ParticipantId, RequestView, Snapshot, ViewerView } from '@gsf/contract';
import { namesFrom, rosterFrom, type Standing } from '@gsf/kit';
import {
  APPLES_REQUESTS,
  type ApplesHostPrivate,
  type ApplesPlayerPrivate,
  type ApplesShared,
} from '@gsf/games';

export interface ApplesView {
  readonly seq: number;
  readonly core: CoreBlock;
  readonly you: ViewerView;
  readonly shared: ApplesShared;
  /** Present only on the host's screen. */
  readonly host: ApplesHostPrivate | null;
  /** Present only on a player's phone. */
  readonly me: ApplesPlayerPrivate | null;

  readonly standings: readonly Standing[];
  readonly requests: readonly RequestView[];

  /** Is this viewer the judge? False on a TV and a host screen, which never are. */
  readonly iAmJudge: boolean;
  readonly judgeName: string | null;

  nameOf(id: ParticipantId | string | null): string | null;
  scoreOf(id: ParticipantId | string | null): number;
  /** The open request with this id, or null. */
  ask(id: string): RequestView | null;
}

export function applesView(snapshot: Snapshot): ApplesView {
  const shared = snapshot.shared as unknown as ApplesShared;
  const kind = snapshot.you.audience.kind;
  const host = kind === 'host' ? (snapshot.private as unknown as ApplesHostPrivate) : null;
  const me = kind === 'player' ? (snapshot.private as unknown as ApplesPlayerPrivate) : null;
  const { core, you } = snapshot;

  const { nameOf, scoreOf } = namesFrom(core);

  return {
    seq: snapshot.seq,
    core,
    you,
    shared,
    host,
    me,
    standings: rosterFrom(core),
    requests: snapshot.requests,
    // `viewPrivate` says so directly, so no surface compares ids.
    iAmJudge: me?.iAmJudge ?? false,
    judgeName: nameOf(shared.judgeId),
    nameOf,
    scoreOf,
    ask: (id) => snapshot.requests.find((request) => request.id === id) ?? null,
  };
}

/** The six ids this game's screens look for, for the round in play. */
export function applesAsks(view: ApplesView): {
  ready: RequestView | null;
  prompt: RequestView | null;
  play: RequestView | null;
  wait: RequestView | null;
  pick: RequestView | null;
  next: RequestView | null;
} {
  const round = view.shared.round;
  return {
    ready: view.ask(APPLES_REQUESTS.ready(round)),
    prompt: view.ask(APPLES_REQUESTS.prompt(round)),
    play: view.ask(APPLES_REQUESTS.play(round)),
    wait: view.ask(APPLES_REQUESTS.wait(round)),
    pick: view.ask(APPLES_REQUESTS.pick(round)),
    next: view.ask(APPLES_REQUESTS.next(round)),
  };
}
